import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import HandArrow from "./HandArrow";
import CategoryTabs from "./CategoryTabs";
import AllergensToggle from "./AllergensToggle";
import ComboStrip from "./ComboStrip";
import { track } from "@/lib/analytics";

const cardVariants = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.07, ease: "easeOut" } }),
};

function FocacciaCard({ p, index }) {
  return (
    <motion.article
      variants={cardVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      custom={index}
      whileHover={{ y: -6, scale: 1.02 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="snap-start shrink-0 w-[78vw] sm:w-auto bg-[#141414] border border-[#242424] rounded-2xl overflow-hidden flex flex-col"
      data-testid={`focaccia-card-${p.slug}`}
    >
      <div className="relative aspect-square overflow-hidden bg-[#0f0f0f]">
        <img
          src={p.image}
          alt={`Focaccia ${p.name} — ${p.ingredients}`}
          className="w-full h-full object-cover"
          loading="lazy"
          onMouseEnter={() => track("product_view", { product: p.slug })}
        />
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display font-black uppercase text-2xl sm:text-3xl text-[#FFC20E] leading-none">{p.name}</h3>
          <button
            aria-label={`Ordina ${p.name}`}
            onClick={() => track("product_view", { product: p.slug, cta: "order" })}
            className="shrink-0 w-9 h-9 rounded-full border-2 border-[#FFC20E] text-[#FFC20E] flex items-center justify-center hover:bg-[#FFC20E] hover:text-[#0C0C0C] transition-colors duration-200"
            data-testid={`focaccia-card-order-btn-${p.slug}`}
          >
            <ArrowRight size={17} />
          </button>
        </div>
        <p className="mt-2 text-sm text-white/70 leading-snug flex-1">{p.ingredients}</p>
        <AllergensToggle product={p} />
        {p.price != null && (
          <span className="mt-3 font-display font-extrabold text-lg text-white">€ {Number(p.price).toFixed(2)}</span>
        )}
      </div>
    </motion.article>
  );
}

export default function FocacceSection({ items, activeCategory, onCategoryChange, settings }) {
  return (
    <section id="menu" className="relative bg-[#0C0C0C] grain py-14 sm:py-20">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <span className="font-display font-bold uppercase tracking-[0.3em] text-sm text-[#FFC20E]">IL MENU</span>
            <h2 className="font-display font-black uppercase display-tight text-5xl sm:text-7xl lg:text-8xl mt-2" data-testid="menu-section-title">
              <span className="text-white">OK. </span>
              <span className="text-[#FFC20E]">COSA </span>
              <span className="text-white">MORDIAMO?</span>
            </h2>
          </div>
          <div className="relative flex items-center gap-2 self-start lg:self-end lg:pb-4">
            <span className="font-hand text-2xl sm:text-3xl font-bold text-[#FFC20E] -rotate-3 leading-tight">
              SCEGLI<br />IL TUO PREFERITO
            </span>
            <HandArrow className="w-14 h-9 rotate-[30deg]" color="#FFC20E" />
          </div>
        </div>

        <div className="mt-8">
          <CategoryTabs active={activeCategory} onChange={onCategoryChange} />
        </div>

        <div className="mt-10 flex sm:grid sm:grid-cols-2 lg:grid-cols-5 gap-4 overflow-x-auto no-scrollbar snap-x-mandatory -mx-4 px-4 sm:mx-0 sm:px-0 pb-2">
          {items.map((p, i) => (
            <FocacciaCard key={p.id} p={p} index={i} />
          ))}
        </div>
        <ComboStrip combo={settings?.combo} />
      </div>
    </section>
  );
}
